import type { CogoApp, CogoBlockDef, CogoBlockManifest, CogoElement, CogoFlow } from "@cogo/react-renderer/core/types"
import { validateNamespaces, resolveBindings } from "@cogo/react-renderer/runtime/namespace-validator"
import { loadDesignTokens, type DesignTokens } from "@cogo/react-renderer/runtime/design-tokens"
import { expandDefinitions } from "@cogo/react-renderer/core/ref-expander"
import { resolveTailwindToStyles } from "@cogo/schema/tailwind-resolver"

export interface LoadedApp {
  manifest: CogoApp
  blocks: Record<string, CogoBlockDef>
  data: Record<string, unknown>
  flows: Record<string, CogoFlow>
  tokens?: DesignTokens
}

/** Guard: only parse response as JSON if content-type is application/json */
function isJsonResponse(res: Response): boolean {
  const ct = res.headers.get("content-type") ?? ""
  return ct.includes("application/json")
}

export async function loadApp(basePath: string): Promise<LoadedApp> {
  const manifestRes = await fetch(`${basePath}/app.cogo.json`)
  if (!manifestRes.ok) throw new Error(`Failed to load manifest: ${manifestRes.status}`)
  const manifest: CogoApp = await manifestRes.json()

  const blocks: Record<string, CogoBlockDef> = {}
  const flows: Record<string, CogoFlow> = {}

  // Collect unique block names across all pages + global logic blocks
  const blockNames = new Set<string>()
  if (manifest.globalBlocks) {
    for (const blockName of manifest.globalBlocks) {
      blockNames.add(blockName)
    }
  }
  for (const page of manifest.pages) {
    for (const blockName of page.blocks) {
      blockNames.add(blockName)
    }
  }

  // Load blocks iteratively — discover sub-blocks referenced via block-ref
  const loaded = new Set<string>()
  while (blockNames.size > loaded.size) {
    const toLoad = Array.from(blockNames).filter((n) => !loaded.has(n))
    if (toLoad.length === 0) break

    await Promise.all(
      toLoad.map(async (blockName) => {
        loaded.add(blockName)
        await loadBlock(basePath, blockName, blocks, flows, blockNames)
      }),
    )
  }

  // Validate logic block namespaces (after all blocks loaded)
  const nsMap = validateNamespaces(blocks)
  const bindingsMap = resolveBindings(blocks, nsMap)

  // Load static datasources from app manifest
  const data: Record<string, unknown> = {}
  await Promise.all(
    Object.entries(manifest.datasources).map(async ([name, ds]) => {
      if (ds.type === "static" && ds.path) {
        try {
          const dsUrl = ds.path.startsWith("/") ? `${basePath}${ds.path}` : `${basePath}/${ds.path}`
          const res = await fetch(dsUrl)
          if (res.ok && isJsonResponse(res)) {
            data[name] = await res.json()
          }
        } catch (err) {
          console.warn(`Failed to load datasource: ${name}`, err)
        }
      }
    }),
  )

  // Load design tokens from theme.json (optional)
  let tokens: DesignTokens | undefined
  const themePath = (manifest as unknown as Record<string, unknown>).themePath as string | undefined
  if (themePath) {
    try {
      const themeRes = await fetch(`${basePath}/${themePath}`)
      if (themeRes.ok && isJsonResponse(themeRes)) {
        const raw = await themeRes.json()
        tokens = loadDesignTokens(raw)
      }
    } catch {
      // theme.json is optional — fall back to defaults
    }
  }

  return { manifest, blocks, data, flows, tokens }
}

/**
 * Load app from a single pre-bundled schema URL (tenant mode).
 * The schema JSON must contain: { manifest, blocks, flows?, data?, tokens? }
 * This avoids N+1 fetch calls — everything in one response.
 */
export async function loadFromSchemaUrl(schemaUrl: string): Promise<LoadedApp> {
  const res = await fetch(schemaUrl)
  if (!res.ok) throw new Error(`Failed to load schema: ${res.status}`)
  const schema = await res.json()

  // Pre-bundled format: { manifest, blocks, flows, data, tokens }
  if (schema.manifest && schema.blocks) {
    const blocks = schema.blocks as Record<string, CogoBlockDef>
    for (const block of Object.values(blocks)) {
      if (block.root) enrichElement(block.root)
    }
    return {
      manifest: schema.manifest,
      blocks,
      data: schema.data ?? {},
      flows: schema.flows ?? {},
      tokens: schema.tokens,
    }
  }

  throw new Error("Schema must be in bundled format { manifest, blocks }")
}

/**
 * Load all 5 block files:
 * 1. ui.cogo.json       — element tree (required)
 * 2. flow.cogo.json     — action flows (optional)
 * 3. variables.cogo.json — state + computed (optional)
 * 4. events.cogo.json   — event bindings (optional)
 * 5. datasource.cogo.json — data sources (optional)
 */
async function loadBlock(
  basePath: string,
  blockName: string,
  blocks: Record<string, CogoBlockDef>,
  flows: Record<string, CogoFlow>,
  blockNames: Set<string>,
): Promise<void> {
  const blockPath = `${basePath}/blocks/${blockName}`

  // 0. block.cogo.json (optional — block manifest)
  let blockManifest: CogoBlockManifest | undefined
  try {
    const manifestRes = await fetch(`${blockPath}/block.cogo.json`)
    if (manifestRes.ok && isJsonResponse(manifestRes)) {
      blockManifest = await manifestRes.json()
    }
  } catch {
    // block.cogo.json is optional — blocks without it are treated as hybrid
  }

  // 1. ui.cogo.json (required)
  try {
    const uiRes = await fetch(`${blockPath}/ui.cogo.json`)
    if (uiRes.ok) {
      const blockDef: CogoBlockDef = expandDefinitions(await uiRes.json())
      // Enrich element tree with resolvedStyles from className
      enrichElement(blockDef.root)
      blocks[blockName] = blockDef

      // Attach manifest if loaded
      if (blockManifest) {
        blocks[blockName].manifest = blockManifest
      }

      // Discover sub-block references
      walkElements(blockDef.root, (el) => {
        if (el.type === "block-ref" && el.blockType && !blockNames.has(el.blockType)) {
          blockNames.add(el.blockType)
        }
      })
    } else {
      console.warn(`Block UI not found: ${blockName}`)
      return
    }
  } catch (err) {
    console.warn(`Failed to load block UI: ${blockName}`, err)
    return
  }

  // 2. flow.cogo.json (optional) — namespaced per block
  try {
    const flowRes = await fetch(`${blockPath}/flow.cogo.json`)
    if (flowRes.ok && isJsonResponse(flowRes)) {
      const flowData = await flowRes.json()
      // Support both formats: { flows: { ... } } and top-level { flowName: { ... } }
      const flowEntries = flowData.flows
        ? Object.entries(flowData.flows)
        : Object.entries(flowData).filter(([, v]) => v && typeof v === "object" && (v as Record<string, unknown>).trigger)
      for (const [flowName, flow] of flowEntries) {
        // Use manifest namespace if available, otherwise use blockName
        const ns = blockManifest?.namespace ?? blockName
        const namespacedKey = `${ns}.${flowName}`
        flows[namespacedKey] = flow as CogoFlow
        // Also register without namespace for backward compatibility
        flows[flowName] = flow as CogoFlow
      }
    }
  } catch {
    // flows are optional
  }

  // 3. variables.cogo.json (optional)
  try {
    const varRes = await fetch(`${blockPath}/variables.cogo.json`)
    if (varRes.ok && isJsonResponse(varRes)) {
      const varData = await varRes.json()
      if (blocks[blockName]) {
        blocks[blockName].variables = varData
      }
    }
  } catch {
    // variables are optional
  }

  // 4. events.cogo.json (optional)
  try {
    const evtRes = await fetch(`${blockPath}/events.cogo.json`)
    if (evtRes.ok && isJsonResponse(evtRes)) {
      const evtData = await evtRes.json()
      if (blocks[blockName]) {
        blocks[blockName].events = evtData
      }
    }
  } catch {
    // events are optional
  }

  // 5. datasource.cogo.json (optional)
  try {
    const dsRes = await fetch(`${blockPath}/datasource.cogo.json`)
    if (dsRes.ok && isJsonResponse(dsRes)) {
      const dsData = await dsRes.json()
      if (blocks[blockName]) {
        blocks[blockName].datasource = dsData
      }
    }
  } catch {
    // datasource are optional
  }

  // 6. manifest.sig verification (optional — infrastructure for Phase 3)
  try {
    const sigRes = await fetch(`${blockPath}/manifest.sig`)
    if (sigRes.ok && isJsonResponse(sigRes)) {
      const manifest = await sigRes.json()
      if (blocks[blockName]) {
        ;(blocks[blockName] as any).__manifestSig = manifest
      }
      console.debug(`[cogo] Block "${blockName}" signature loaded`)
    }
  } catch {
    // manifest.sig is optional in development
  }
}

function walkElements(element: CogoElement, fn: (el: CogoElement) => void): void {
  fn(element)
  element.children?.forEach((child) => walkElements(child, fn))
}

/**
 * Enrich an element tree with resolvedStyles by converting className
 * to platform-agnostic style values. Skips elements that already have
 * resolvedStyles (e.g., pre-built blocks).
 */
export function enrichElement(el: CogoElement): CogoElement {
  if (el.className && !el.resolvedStyles) {
    el.resolvedStyles = resolveTailwindToStyles(el.className)
  }
  if (el.children) {
    el.children = el.children.map(enrichElement)
  }
  return el
}
